import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import styles from './ActionPointModal.module.scss';
import {
  IconX,
  IconTrash
} from '@tabler/icons-react';

import { Status, ActionPointDto, TenantUserDto } from '../../api/model';
import { usePutActionpointId, useDeleteActionpointId } from '../../api/action-point-api/action-point-api';
import { useGetProjectIdMembers } from '../../api/project-api/project-api';
import { useNotifications } from '../../hooks/useNotifications';
import ConfirmationDialog from '../ui/ConfirmationDialog/ConfirmationDialog';
import UserPicker from '../ui/UserPicker/UserPicker';

interface EditActionPointModalProps {
  isOpen: boolean;
  onClose: () => void;
  onActionPointUpdated?: () => void;
  actionPointData?: ActionPointDto;
}

interface ActionPointFormData {
  name: string;
  comment: string;
  validationCriteria: string;
  validationMethod: string;
  assignedUserIds: number[];
  status: Status;
}

const EditActionPointModal: React.FC<EditActionPointModalProps> = ({
  isOpen,
  onClose,
  onActionPointUpdated,
  actionPointData
}) => {
  const { t } = useTranslation('actionPoints');
  const [formData, setFormData] = useState<ActionPointFormData>({
    name: '',
    comment: '',
    validationCriteria: '',
    validationMethod: '',
    assignedUserIds: [],
    status: Status.PLANNED
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // Hooks
  const updateActionPointMutation = usePutActionpointId();
  const deleteActionPointMutation = useDeleteActionpointId();
  const { notifySuccess, notifyError } = useNotifications();
  const projectId = actionPointData?.projectId;
  const { data: projectMembers } = useGetProjectIdMembers(projectId ?? 0, {
    query: { enabled: !!projectId },
  });
  const memberUsers = React.useMemo<TenantUserDto[]>(
    () => (projectMembers ?? []).map(m => ({
      id: m.userId, username: m.username, firstName: m.firstName,
      lastName: m.lastName, tenantRoleDisplayName: m.tenantRoleDisplayName,
    })),
    [projectMembers]
  );

  // Initialize form data
  useEffect(() => {
    if (actionPointData) {
      setFormData({
        name: actionPointData.name || '',
        comment: actionPointData.comment || '',
        validationCriteria: actionPointData.validationCriteria || '',
        validationMethod: actionPointData.validationMethod || '',
        assignedUserIds: actionPointData.assignedUserIds ?? [],
        status: actionPointData.status || Status.PLANNED
      });
    }
  }, [actionPointData]);

  const handleInputChange = (field: keyof ActionPointFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = (): string | null => {
    if (!formData.name.trim()) {
      return t('create.validation.nameRequired');
    }
    if (!formData.comment.trim()) {
      return t('create.validation.commentRequired');
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!actionPointData || !actionPointData.id) {
      notifyError(t('edit.title'), t('edit.validation.noUpdateData'));
      return;
    }

    const validationError = validateForm();
    if (validationError) {
      notifyError(t('edit.title'), validationError);
      return;
    }

    setIsSubmitting(true);

    try {
      const updatedActionPointData: ActionPointDto = {
        id: actionPointData.id,
        name: formData.name,
        comment: formData.comment,
        validationCriteria: formData.validationCriteria || undefined,
        validationMethod: formData.validationMethod || undefined,
        assignedUserIds: formData.assignedUserIds,
        status: formData.status,
        projectId: actionPointData.projectId,
        created: actionPointData.created,
        modified: actionPointData.modified
      };

      await updateActionPointMutation.mutateAsync({
        id: actionPointData.id,
        data: updatedActionPointData
      });

      notifySuccess(t('edit.title'), t('edit.validation.updateSuccess', { name: formData.name }));
      onActionPointUpdated?.();
      onClose();
    } catch (error) {
      console.error('Error updating action point:', error);
      notifyError(t('edit.title'), error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!actionPointData || !actionPointData.id) {
      notifyError(t('edit.deleteDialog.title'), t('edit.validation.noData'));
      return;
    }

    setIsSubmitting(true);

    try {
      await deleteActionPointMutation.mutateAsync({
        id: actionPointData.id
      });

      notifySuccess(t('edit.deleteDialog.title'), t('edit.validation.deleteSuccess', { name: formData.name }));
      onActionPointUpdated?.();
      onClose();
    } catch (error) {
      console.error('Error deleting action point:', error);
      notifyError(t('edit.deleteDialog.title'), error);
    } finally {
      setIsSubmitting(false);
      setIsDeleteDialogOpen(false);
    }
  };

  if (!isOpen || !actionPointData) return null;

  return (
    <>
      <div className={styles.modalOverlay} onClick={onClose}>
        <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <div className={styles.headerContent}>
              <h2 className={styles.modalTitle}>{t('edit.title')}</h2>
              <p className={styles.headerDescription}>{t('edit.description')}</p>
            </div>
            <button className={styles.closeButton} onClick={onClose} type="button">
              <IconX size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className={styles.modalBody}>
              <div className={styles.formSection}>
                <h3 className={styles.sectionTitle}>{t('create.sections.basic')}</h3>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    {t('create.fields.name').replace(' *','')}
                    <span className={styles.required}>*</span>
                  </label>
                  <input
                    type="text"
                    className={styles.formInput}
                    placeholder={t('create.fields.namePlaceholder')}
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    disabled={isSubmitting}
                    required
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    {t('create.fields.comment').replace(' *','')}
                    <span className={styles.required}>*</span>
                  </label>
                  <textarea
                    className={styles.formTextarea}
                    placeholder={t('create.fields.commentPlaceholder')}
                    value={formData.comment}
                    onChange={(e) => handleInputChange('comment', e.target.value)}
                    disabled={isSubmitting}
                    rows={4}
                    required
                  />
                </div>
              </div>

              <div className={styles.formSection}>
                <h3 className={styles.sectionTitle}>{t('create.sections.validation')}</h3>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                      {t('create.fields.validationCriteria')}
                    </label>
                    <input
                      type="text"
                      className={styles.formInput}
                      placeholder={t('create.fields.validationCriteriaPlaceholder')}
                      value={formData.validationCriteria}
                      onChange={(e) => handleInputChange('validationCriteria', e.target.value)}
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                      {t('create.fields.validationMethod')}
                    </label>
                    <input
                      type="text"
                      className={styles.formInput}
                      placeholder={t('create.fields.validationMethodPlaceholder')}
                      value={formData.validationMethod}
                      onChange={(e) => handleInputChange('validationMethod', e.target.value)}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
              </div>

              <div className={styles.formSection}>
                <h3 className={styles.sectionTitle}>{t('create.sections.assignment')}</h3>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    {t('create.fields.assignedPerson')}
                  </label>
                  <UserPicker
                    multi
                    value={formData.assignedUserIds}
                    onChange={(ids) => handleInputChange('assignedUserIds', ids)}
                    disabled={isSubmitting}
                    placeholder={t('create.fields.assignedPersonPlaceholder')}
                    users={projectId ? memberUsers : undefined}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    {t('create.fields.status')}
                  </label>
                  <select
                    className={styles.formSelect}
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value as Status)}
                    disabled={isSubmitting}
                  >
                    <option value={Status.PLANNED}>{t('status.planned')}</option>
                    <option value={Status.IN_PROGRESS}>{t('status.inProgress')}</option>
                    <option value={Status.DELAYED}>{t('status.delayed')}</option>
                    <option value={Status.FINISHED}>{t('status.finished')}</option>
                  </select>
                </div>
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button
                type="button"
                className={styles.deleteButton}
                onClick={() => setIsDeleteDialogOpen(true)}
                disabled={isSubmitting}
              >
                <IconTrash size={18} />
                {t('edit.buttons.delete')}
              </button>

              <div className={styles.footerActions}>
                <button
                  type="button"
                  className={styles.cancelButton}
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  {t('edit.buttons.cancel')}
                </button>
                <button
                  type="submit"
                  className={styles.submitButton}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t('edit.buttons.updating') : t('edit.buttons.update')}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      <ConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title={t('edit.deleteDialog.title')}
        message={t('edit.deleteDialog.message', { name: formData.name })}
        confirmText={t('edit.deleteDialog.confirm')}
        cancelText={t('edit.deleteDialog.cancel')}
        type="danger"
      />
    </>
  );
};

export default EditActionPointModal;
